import React, {useEffect, useState} from "react";
import CurrencyFormat from "react-currency-format";
import moment from "moment/moment";
import { Trash2, ChevronDown, ChevronUp } from "react-feather";
import { getSupplierMaterialImports, setDeleteMaterialImport} from "../../../actions/supplier";
import {useDispatch, useSelector} from "react-redux";
import SupplierMaterialImportDeleteForm from "../SupplierMaterialImportDeleteForm";
import {Pagination} from "react-bootstrap";
import useQueryParams from "../../../utils/hooks/useQueryParams";
import {getParams} from "../../../utils/hooks/getParams";
import {useLocation} from "react-router-dom";
import { usePermission } from '../../../hooks/usePermission';

const SupplierMaterialImports = ({supplier, supplier_material_imports}) => {

    const dispatch = useDispatch();
    const location = useLocation();
    const {materialImportsNumberOfPages, materialImportsCurrentPage} = useSelector(state => state.supplier);
    const queryParams = useQueryParams();
    const { userCan } = usePermission();

    const {id} = supplier;
    const [currentPage, setCurrentPage] = useState(parseInt(queryParams.page) || 1);
    const [filters, setFilters] = useState(() => {
        const params = getParams(true);
        return {
            from_date: params.from_date || '',
            to_date: params.to_date || '',
            page: parseInt(params.page) || 1
        };
    });
    
    // State to track expanded import rows
    const [expandedImports, setExpandedImports] = useState({});

    useEffect(() => {
        if (id) {
            const params = getParams(true);
            dispatch(getSupplierMaterialImports(id, {...filters, page: currentPage}));
        }
    }, [dispatch, id, currentPage]);

    // Update state when URL changes
    useEffect(() => {
        const params = getParams(true);
        if (params.page && parseInt(params.page) !== currentPage) {
            setCurrentPage(parseInt(params.page));
        }
        
        // Update filters from URL
        const newFilters = {...filters};
        let changed = false;
        
        if (params.from_date !== filters.from_date) {
            newFilters.from_date = params.from_date || '';
            changed = true;
        }
        
        if (params.to_date !== filters.to_date) {
            newFilters.to_date = params.to_date || '';
            changed = true;
        }
        
        if (changed) {
            setFilters(newFilters);
        }
    }, [location.search]);


    const [from_date, setFromDate] = useState(filters.from_date || '');
    const [to_date, setToDate] = useState(filters.to_date || '');

    const onChangeFromDate = (event) => {
        const value = event.target.value;
        setFromDate(value);
        
        // Update URL
        const url = new URL(window.location);
        if (value) {
            url.searchParams.set('from_date', value);
        } else {
            url.searchParams.delete('from_date');
        }
        url.searchParams.set('page', 1);
        window.history.pushState({}, '', url);
        
        const newFilters = {
            ...filters,
            from_date: value,
            page: 1
        };
        setFilters(newFilters);
        setCurrentPage(1);
    };

    const onChangeToDate = (event) => {
        const value = event.target.value;
        setToDate(value);
        
        // Update URL
        const url = new URL(window.location);
        if (value) {
            url.searchParams.set('to_date', value);
        } else {
            url.searchParams.delete('to_date');
        }
        url.searchParams.set('page', 1);
        window.history.pushState({}, '', url);
        
        const newFilters = {
            ...filters,
            to_date: value,
            page: 1
        };
        setFilters(newFilters);
        setCurrentPage(1);
    };

    const onPageChange = (page) => {
        setCurrentPage(page);
        
        // Update URL
        const url = new URL(window.location);
        url.searchParams.set('page', page);
        window.history.pushState({}, '', url);
    };
    
    const renderPagination = () => {
        let items = [];
        const totalPages = materialImportsNumberOfPages || 1;
        
        if (totalPages <= 1) return null;

        items.push(
            <Pagination.Prev 
                key="prev" 
                onClick={() => onPageChange(Math.max(1, currentPage - 1))} 
                disabled={currentPage === 1}
            />
        );
        
        // Add first page
        items.push(
            <Pagination.Item
                key={1}
                active={1 === currentPage}
                onClick={() => onPageChange(1)}
            >
                1
            </Pagination.Item>
        );

        // Add ellipsis if needed
        if (currentPage > 3) {
            items.push(<Pagination.Ellipsis key="ellipsis1"/>);
        }

        // Add pages around current page
        for (let i = Math.max(2, currentPage - 1); i <= Math.min(totalPages - 1, currentPage + 1); i++) {
            if (i <= 1 || i >= totalPages) continue;
            items.push(
                <Pagination.Item
                    key={i}
                    active={i === currentPage}
                    onClick={() => onPageChange(i)}
                >
                    {i}
                </Pagination.Item>
            );
        }

        // Add ellipsis if needed
        if (currentPage < totalPages - 2) {
            items.push(<Pagination.Ellipsis key="ellipsis2"/>);
        }

        // Add last page
        if (totalPages > 1) {
            items.push(
                <Pagination.Item
                    key={totalPages}
                    active={totalPages === currentPage}
                    onClick={() => onPageChange(totalPages)}
                >
                    {totalPages}
                </Pagination.Item>
            );
        }
        
        items.push(
            <Pagination.Next 
                key="next" 
                onClick={() => onPageChange(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage === totalPages} 
            />
        );

        return items;
    };

    const toggleExpandImport = (importId) => {
        setExpandedImports(prev => ({
            ...prev,
            [importId]: !prev[importId]
        }));
    };

    const onDeleteMaterialImport = (import_id) => {
        dispatch(setDeleteMaterialImport(import_id));
    };

    // Calculate totals
    const calculateImportTotals = () => {
        if (!supplier_material_imports || !supplier_material_imports.length) {
            return { totalQuantity: 0, totalAmount: 0 };
        }

        return supplier_material_imports.reduce((acc, item) => {
            // If it has items (bulk import), sum all items
            if (item.items && Array.isArray(item.items)) {
                const itemTotals = item.items.reduce((itemAcc, subItem) => {
                    return {
                        totalQuantity: itemAcc.totalQuantity + parseFloat(subItem.quantity || 0),
                        totalAmount: itemAcc.totalAmount + (parseFloat(subItem.quantity || 0) * parseFloat(subItem.price || 0))
                    };
                }, { totalQuantity: 0, totalAmount: 0 });
                
                return {
                    totalQuantity: acc.totalQuantity + itemTotals.totalQuantity,
                    totalAmount: acc.totalAmount + itemTotals.totalAmount
                };
            } 
            // Legacy format (single item per import)
            else {
                return {
                    totalQuantity: acc.totalQuantity + parseFloat(item.quantity || 0),
                    totalAmount: acc.totalAmount + parseFloat(item.total_amount || 0)
                };
            }
        }, { totalQuantity: 0, totalAmount: 0 });
    };

    const totals = calculateImportTotals();

    return (
        <div className="w-100 table-responsive px-2">

            <div className="row mb-3">
                <div className="col-md-6"></div>
                <div className="col-md-6">
                    <div className="crud_actions text-end">
                        {/*<button className="btn btn-primary"*/}
                        {/*        onClick={() => handleShowMaterial()}>*/}
                        {/*    Material import*/}
                        {/*    <Plus/>*/}
                        {/*</button>*/}
                    </div>
                </div>
            </div>


            <div
                className="mb-2 align-items-center d-flex justify-content-between">

                <div className="mb-3">
                    <label htmlFor="date1" className="mb-2">dan</label>
                    <input onChange={onChangeFromDate}
                           value={from_date}
                           className="form-control"
                           id="date1" type="date"/>
                </div>

                <div className="mb-3">
                    <label htmlFor="date2" className="mb-2">gacha</label>
                    <input onChange={onChangeToDate}
                           value={to_date}
                           className="form-control"
                           id="date2" type="date"/>
                </div>
            </div>


            <table className="table custom-table table-hover">
                <thead className="table-dark">
                <tr>
                    <th scope="col" style={{width: "30px"}}></th>
                    <th scope="col">#</th>
                    <th scope="col">Material nomi</th>
                    <th scope="col">Maxsus Import?</th>
                    <th scope="col">Miqdori</th>
                    <th scope="col">Narxi</th>
                    <th scope="col">Summa</th>
                    <th scope="col">Sana</th>
                    <th scope="col">Amallar</th>
                </tr>
                </thead>
                <tbody>
                {
                    supplier_material_imports?.map((item, index) => {
                        const isBulkImport = item.items && Array.isArray(item.items) && item.items.length > 0;
                        const isExpanded = expandedImports[item.id];
                        
                        // Determine what to show in the main row
                        let displayName = "";
                        let displayQuantity = 0;
                        let displayPrice = 0;
                        let displayTotal = 0;
                        
                        if (isBulkImport) {
                            // For bulk imports, show count of items and total
                            displayName = `${item.items_count || item.items.length} materiallar`;
                            displayQuantity = item.items.reduce((sum, i) => sum + parseFloat(i.quantity || 0), 0);
                            displayTotal = item.total_amount || item.items.reduce((sum, i) => sum + (parseFloat(i.quantity || 0) * parseFloat(i.price || 0)), 0);
                        } else {
                            // Legacy format
                            displayName = item.material?.name || "Unknown";
                            displayQuantity = parseFloat(item.quantity || 0);
                            displayPrice = parseFloat(item.price || 0);
                            displayTotal = parseFloat(item.total_amount || displayQuantity * displayPrice);
                        }
                        
                        return (
                            <React.Fragment key={index}>
                                <tr>
                                    <td>
                                        {isBulkImport && (
                                            <button
                                                onClick={() => toggleExpandImport(item.id)}
                                                className="btn btn-sm btn-light p-0"
                                            >
                                                {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                                            </button>
                                        )}
                                    </td>
                                    <th scope="row">{index + 1}</th>
                                    <th>{displayName}</th>
                                    <td>{item.is_for_special_sale ? 'Ha' : 'Yo\'q'}</td>
                                    <th>
                                        {displayQuantity}
                                    </th>
                                    <td>
                                        {!isBulkImport && (
                                            <CurrencyFormat value={displayPrice}
                                                displayType={'text'}
                                                thousandSeparator={true}
                                                suffix={"  so'm"}/>
                                        )}
                                        {isBulkImport && "-"}
                                    </td>
                                    <td>
                                        <CurrencyFormat
                                            value={displayTotal}
                                            displayType={'text'}
                                            thousandSeparator={true}
                                            suffix={"  so'm"}/>
                                    </td>
                                    <td>{moment(item.date).format('YYYY-MM-DD')}</td>
                                    <td>
                                        <div className="actions">
                                            {userCan('delete material import') && (
                                                <Trash2
                                                    onClick={() => onDeleteMaterialImport(item.id)}
                                                />
                                            )}
                                        </div>
                                    </td>
                                </tr>
                                
                                {/* Sub-rows for bulk import items */}
                                {isBulkImport && isExpanded && item.items.map((subItem, subIndex) => (
                                    <tr key={`${index}-${subIndex}`} className="bg-light border-bottom">
                                        <td></td>
                                        <td></td>
                                        <td className="ps-4">{subItem.material?.name}</td>
                                        <td></td>
                                        <td>{parseFloat(subItem.quantity || 0)}</td>
                                        <td>
                                            <CurrencyFormat value={subItem.price}
                                                displayType={'text'}
                                                thousandSeparator={true}
                                                suffix={"  so'm"}/>
                                        </td>
                                        <td>
                                            <CurrencyFormat 
                                                value={parseFloat(subItem.quantity || 0) * parseFloat(subItem.price || 0)}
                                                displayType={'text'}
                                                thousandSeparator={true}
                                                suffix={"  so'm"}/>
                                        </td>
                                        <td></td>
                                        <td></td>
                                    </tr>
                                ))}
                            </React.Fragment>
                        );
                    })
                }
                {
                    <tr key={'total'}>
                        <th scope="row" colSpan="2">#</th>
                        <th>*</th>
                        <th>*</th>
                        <th>
                            {totals.totalQuantity}
                        </th>
                        <td>
                            *
                        </td>
                        <td>
                            <CurrencyFormat
                                value={totals.totalAmount}
                                displayType={'text'}
                                thousandSeparator={true}
                                suffix={"  so'm"}/>
                        </td>
                        <td>*</td>
                        <td>*</td>
                    </tr>
                }
                </tbody>
            </table>
            
            {/* Pagination controls */}
            <div className="d-flex justify-content-center mt-3">
                <Pagination>
                    {renderPagination()}
                </Pagination>
            </div>
            
            <SupplierMaterialImportDeleteForm supplier_id={id}/>
        </div>
    )
}

export default SupplierMaterialImports;